import styled, { keyframes, css } from 'styled-components';

/* ── paleta base (herda do design system de conceitos/calendário) ── */
const TEAL = 'var(--teal)';
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)';
const TEAL_SOFT = 'var(--teal-soft)';
const GRAY_BG = 'var(--bg)';
const GRAY_LINE = 'var(--line)';
const GRAY_TEXT = 'var(--text-muted)';
const GREEN = 'var(--green)';
const GREEN_SOFT = 'var(--green-soft)';
const RED = 'var(--red)';
const RED_SOFT = 'var(--red-soft)';
const AMBER = 'var(--amber, #d97706)';
const AMBER_SOFT = '#fdf2e0';
const BLUE = '#2a6fb0';
const BLUE_SOFT = '#e7f0f9';

/* ── acentos próprios desta página ── */
//const CORAL = 'var(--coral)';
const TEAL_GLOW = 'var(--teal-glow)';

const bp = { desktop: '1024px', tablet: '768px', mobile: '540px', small: '380px' };

/* ── animações ── */
const expand = keyframes`
  from { opacity: 0; transform: translateY(-6px); max-height: 0; }
  to   { opacity: 1; transform: translateY(0);    max-height: 9999px; }
`;
const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(29, 139, 90, 0.5); }
  50%      { box-shadow: 0 0 0 5px rgba(29, 139, 90, 0); }
`;

/* ── Layout base ── */
export const Container = styled.div`
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;
  background:
    radial-gradient(1200px 400px at 50% -120px, var(--bg-glow) 0%, transparent 70%),
    ${GRAY_BG};
  padding: 16px;
  display: flex;
  justify-content: center;
  @media (max-width: ${bp.tablet}) { padding: 12px; }
  @media (max-width: ${bp.mobile}) { padding: 8px; }
`;

export const ContainerDivs = styled.div`
  width: 100%;
  max-width: 960px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 14px;
  @media (max-width: ${bp.mobile}) { gap: 10px; }
`;

/* ── Botão base reutilizável ── */
const ActionBase = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  border-radius: 10px;
  padding: 9px 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: transform 0.12s ease, filter 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.55; cursor: not-allowed; transform: none; filter: none; }
  @media (max-width: ${bp.mobile}) { font-size: 12px; padding: 9px 14px; }
`;

/* ── HERO / Cabeçalho da turma ── */
export const Header = styled.div`
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  width: 100%;
  padding: 24px 26px 22px;
  background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 24px ${TEAL_GLOW};
  animation: ${fadeUp} 0.4s ease;
  &::before, &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.06);
    pointer-events: none;
  }
  &::before { width: 190px; height: 190px; top: -80px; right: -40px; }
  &::after  { width: 130px; height: 130px; bottom: -70px; right: 110px; }
  @media (max-width: ${bp.mobile}) { padding: 18px 18px 16px; border-radius: 16px; }
`;

export const HeaderTop = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  @media (max-width: ${bp.mobile}) { flex-direction: column; align-items: stretch; }
`;

export const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const SchoolTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  opacity: 0.82;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 10px;
  word-break: break-word;
  @media (max-width: ${bp.mobile}) { font-size: 19px; }
  @media (max-width: ${bp.small}) { font-size: 17px; }
`;

export const Subtitle = styled.span`
  font-size: 13px;
  opacity: 0.85;
  font-weight: 500;
  @media (max-width: ${bp.mobile}) { font-size: 12px; }
`;

/* chips de metadados (série, turno, etc.) dentro do hero */
export const MetaRow = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const MetaChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.13);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 10px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(4px);
  b { font-weight: 800; opacity: 0.7; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; margin-right: 2px; }
`;

export const HeaderActions = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  @media (max-width: ${bp.mobile}) { width: 100%; }
`;

export const EditButton = styled(ActionBase)`
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  backdrop-filter: blur(4px);
  @media (max-width: ${bp.mobile}) { flex: 1; }
`;

/* ── Rótulo de seção ── */
export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 8px 2px 0;
  color: ${GRAY_TEXT};
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  &::after { content: ''; flex: 1; height: 1px; background: ${GRAY_LINE}; }
`;

/* ── Card genérico (painéis) ── */
export const Card = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeUp} 0.35s ease backwards;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
  &:hover { box-shadow: 0 5px 16px rgba(0, 0, 0, 0.05); }
  @media (max-width: ${bp.mobile}) { padding: 14px 14px; border-radius: 12px; }
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CardTitle = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: ${TEAL_TEXT};
  display: flex;
  align-items: center;
  gap: 8px;
  .em { font-size: 17px; }
`;

export const CountTag = styled.span`
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  padding: 3px 11px;
`;

/* ── Linha de ação rápida (links de impressão) ── */
export const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const LinkPill = styled.button`
  border: 1px solid #d9d3ee;
  background: #f3effc;
  color: #6b3fc4;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 7px 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, transform 0.12s ease;
  &:hover { background: #ebe3fa; transform: translateY(-1px); }
`;

/* ── Lista de pessoas (professores / alunos) ── */
export const PeopleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const PersonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  background: ${GRAY_BG};
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
  &:hover { background: ${TEAL_SOFT}; border-color: #cfe3e9; transform: translateX(2px); }
  .avatar {
    width: 32px; height: 32px;
    flex-shrink: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    display: flex; align-items: center; justify-content: center;
    text-transform: uppercase;
  }
  .name {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-2);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chev { color: #b7c2c7; font-size: 16px; flex-shrink: 0; }
  @media (max-width: ${bp.mobile}) { padding: 10px 12px; .name { font-size: 13px; } }
`;

export const StatusBadge = styled.span`
  flex-shrink: 0;
  font-size: 10.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 999px;
  padding: 3px 9px;
  ${({ $tone }) => $tone === 'red' && css`background: ${RED_SOFT}; color: ${RED};`}
  ${({ $tone }) => $tone === 'blue' && css`background: ${BLUE_SOFT}; color: ${BLUE};`}
  ${({ $tone }) => $tone === 'amber' && css`background: ${AMBER_SOFT}; color: ${AMBER};`}
`;

export const EmptyHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 16px;
  border: 1px dashed ${GRAY_LINE};
  border-radius: 11px;
  color: ${GRAY_TEXT};
  font-size: 13px;
  background: ${GRAY_BG};
  .em { font-size: 20px; }
`;

/* botões de gerência das pessoas (adicionar / remover etc.) */
export const ManageRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Btt02 = styled(ActionBase)`
  background: ${TEAL_SOFT};
  color: ${TEAL_TEXT};
  border: 1px solid var(--teal-soft);
`;

export const AddBtn = styled(ActionBase)`
  background: ${TEAL};
  color: #fff;
  box-shadow: 0 2px 8px ${TEAL_GLOW};
`;

export const RemoveBtn = styled(ActionBase)`
  background: ${RED_SOFT};
  color: ${RED};
  border: 1px solid #f1cdcd;
`;

/* ── Diário da turma ── */
export const DiaryWrapper = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: ${fadeUp} 0.35s ease backwards;
  @media (max-width: ${bp.mobile}) { padding: 14px; border-radius: 12px; }
`;

export const DiaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  @media (max-width: ${bp.mobile}) { grid-template-columns: 1fr; }
`;

export const DiaryBimester = styled.div`
  border: 1px solid ${GRAY_LINE};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${GRAY_BG};
  transition: box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease;
  &:hover { box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06); transform: translateY(-1px); border-color: #cfe0e6; }

  /* Quando é o card "órfão" (ex.: 4º bimestre sozinho na última linha):
     ocupa as duas colunas, mas fica centralizado e um pouco mais largo,
     formando um triângulo de cabeça para baixo (2 em cima, 1 no centro). */
  ${({ $centered }) => $centered && css`
    grid-column: 1 / -1;
    width: 60%;
    justify-self: center;
    @media (max-width: ${bp.mobile}) { width: 100%; }
  `}

  h3 {
    margin: 0;
    font-size: 13.5px;
    font-weight: 800;
    color: ${TEAL_TEXT};
    display: flex;
    align-items: center;
    gap: 7px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
`;

/* card de "resultado final" destacado */
export const FinalCard = styled(DiaryBimester)`
  background: linear-gradient(135deg, ${GREEN_SOFT} 0%, #f3fbf7 100%);
  border-color: #bfe6d2;
  grid-column: 1 / -1;
`;

export const StatusLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const TeacherStatus = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .role { font-size: 12.5px; font-weight: 600; color: ${GRAY_TEXT}; }
  .btns { display: flex; gap: 6px; flex-wrap: wrap; }
`;

export const StateChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-radius: 999px;
  padding: 3px 10px;
  ${({ $open }) => $open
    ? css`background: ${GREEN_SOFT}; color: ${GREEN}; &::before { content: '●'; font-size: 8px; animation: ${pulse} 2s infinite; border-radius: 50%; }`
    : css`background: ${RED_SOFT}; color: ${RED}; &::before { content: '✓'; }`}
`;

/* botões pequenos dentro do diário */
export const MiniBtn = styled.button`
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: filter 0.15s ease, transform 0.12s ease, background 0.15s ease;
  &:hover { filter: brightness(1.05); transform: translateY(-1px); }
  &:active { transform: translateY(0); }
  ${({ $variant }) => $variant === 'close' && css`background: ${AMBER}; color: #fff;`}
  ${({ $variant }) => $variant === 'reopen' && css`background: ${BLUE_SOFT}; color: ${BLUE}; border: 1px solid #cfe0f2;`}
  ${({ $variant }) => $variant === 'see' && css`background: ${TEAL}; color: #fff;`}
  /* prévia do diário aberto: laranja tracejado (mesma cor da faixa "PRÉVIA"), para não confundir com o diário oficial */
  ${({ $variant }) => $variant === 'preview' && css`background: #fff4e5; color: #b45309; border: 1px dashed #b45309;`}
  ${({ $variant }) => $variant === 'final' && css`background: ${GREEN}; color: #fff; padding: 9px 18px; font-size: 13px;`}
`;

export const DiaryEmpty = styled.div`
  text-align: center;
  padding: 30px 16px;
  color: ${GRAY_TEXT};
  font-size: 13px;
  border: 1px dashed ${GRAY_LINE};
  border-radius: 12px;
  background: ${GRAY_BG};
  .em { display: block; font-size: 28px; margin-bottom: 6px; }
`;

/* ── Botões de supervisor ── */
export const SupervisorAddButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  & > button {
    background: var(--surface);
    border: 1px solid ${GRAY_LINE};
    color: ${TEAL_TEXT};
    padding: 13px 16px;
    border-radius: 12px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 700;
    font-family: inherit;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
    &:hover {
      background: ${TEAL};
      color: #fff;
      border-color: ${TEAL};
      transform: translateY(-2px);
      box-shadow: 0 6px 16px ${TEAL_GLOW};
    }
    @media (max-width: ${bp.mobile}) { font-size: 13px; padding: 12px 14px; }
  }
`;

/* ── Skeleton ── */
const skeletonBase = css`
  background: linear-gradient(90deg, var(--skel-a) 0%, var(--skel-b) 50%, var(--skel-a) 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
  border-radius: 8px;
`;
export const SkeletonLine = styled.div`
  ${skeletonBase}
  height: ${({ $h }) => $h || '14px'};
  width: ${({ $w }) => $w || '100%'};
  margin: 4px 0;
`;
export const SkeletonCard = styled.div`
  background: var(--surface);
  border: 1px solid ${GRAY_LINE};
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
export const SkeletonHero = styled.div`
  ${skeletonBase}
  height: 150px;
  border-radius: 18px;
  background: linear-gradient(90deg, #dde7ea 0%, #e9f0f2 50%, #dde7ea 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.3s linear infinite;
`;

/* ── Modais ── */
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 30, 38, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
  animation: ${fadeIn} 0.15s ease;
  overscroll-behavior: contain;
  @media (max-width: ${bp.mobile}) { padding: 0; align-items: flex-end; }
`;

export const Modal = styled.div`
  background: var(--surface);
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
  animation: ${expand} 0.2s ease;
  max-height: 88vh;
  overflow-y: auto;
  @media (max-width: ${bp.mobile}) { border-radius: 18px 18px 0 0; padding: 22px 18px; max-width: 100%; }
`;

export const ModalTitle = styled.h3`
  margin: 0 0 12px;
  color: ${({ $tone }) => ($tone === 'danger' ? RED : AMBER)};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export const ModalText = styled.p`
  margin: 0 0 20px;
  color: ${GRAY_TEXT};
  font-size: 14px;
  line-height: 1.55;
  text-align: left;
  strong { color: ${TEAL_TEXT}; }
  &.danger strong { color: ${RED}; }
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  @media (max-width: ${bp.small}) { flex-direction: column-reverse; & > button { width: 100%; } }
`;

export const ConfirmButton = styled(ActionBase)`
  background: ${GREEN};
  color: #fff;
  padding: 10px 20px;
`;

export const ConfirmDanger = styled(ActionBase)`
  background: ${RED};
  color: #fff;
  padding: 10px 20px;
`;

export const CancelButton = styled(ActionBase)`
  background: var(--surface-2);
  color: ${GRAY_TEXT};
  border: 1px solid ${GRAY_LINE};
  padding: 10px 20px;
`;

/* lista de seleção dentro do modal (supervisor escolhe funcionário) */
export const SelectList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  li {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: 1px solid ${GRAY_LINE};
    border-radius: 11px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-2);
    background: ${GRAY_BG};
    transition: background 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
    &:hover { background: ${TEAL_SOFT}; border-color: #cfe3e9; transform: translateX(2px); }
    .avatar {
      width: 30px; height: 30px; border-radius: 50%;
      background: linear-gradient(135deg, ${TEAL} 0%, ${TEAL_LIGHT} 100%);
      color: #fff; font-size: 12px; font-weight: 800;
      display: flex; align-items: center; justify-content: center;
      text-transform: uppercase; flex-shrink: 0;
    }
  }
`;

/* select estilizado (modal de boletins) */
export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: left;
  margin-bottom: 18px;
`;

export const Label = styled.label`
  font-weight: 700;
  font-size: 13px;
  color: ${TEAL_TEXT};
`;

export const Select = styled.select`
  padding: 12px 14px;
  border: 1px solid ${GRAY_LINE};
  border-radius: 11px;
  font-size: 14px;
  font-family: inherit;
  color: var(--text-2);
  background: ${GRAY_BG};
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus { outline: none; border-color: ${TEAL_LIGHT}; box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.12); }
`;